import { useCallback, useEffect, useState } from 'react';
import { FormularioCaso } from '../componentes/FormularioCaso.tsx';
import { FormularioCliente } from '../componentes/FormularioCliente.tsx';
import { FormularioContrato } from '../componentes/FormularioContrato.tsx';
import { RegistrarReuniao } from '../componentes/RegistrarReuniao.tsx';
import { CorrigirCobrancas } from '../componentes/CorrigirCobrancas.tsx';
import { Investigados } from '../componentes/Investigados.tsx';
import { FundoDeCustas } from '../componentes/FundoDeCustas.tsx';
import { AceitarProposta } from '../componentes/AceitarProposta.tsx';
import { PedidosDoCliente, TrazerDoPedido, type PropostaDoPedido } from '../componentes/PedidosDoCliente.tsx';
import { RecebidoDoCliente } from '../componentes/DocumentosDoPedido.tsx';
import {
  SecaoAnotacoes,
  SecaoContatos,
  SecaoContratos,
} from '../componentes/SecoesDaFicha.tsx';
import {
  ErroDaApi,
  api,
  apiCasos,
  apiClientes,
  apiContratos,
  apiFormulario,
  apiRelatorios,
  type Caso,
  type ContaDeDocumentos,
  type Contrato,
  type FaltaDoPedido,
  type Ficha,
  type SituacaoCaso,
  type SituacaoContrato,
} from '../lib/api.ts';
import { Confirmar } from '../componentes/Confirmar.tsx';
import { Secao } from '../componentes/Secao.tsx';
import { avisar, avisarErro } from '../componentes/Avisos.tsx';
import { paraTela } from '../lib/datas.ts';
import { PercursoDoCaso, PrazoNaLinha } from '../componentes/PeleDoCaso.tsx';
import { usarEhMaster } from '../lib/papel.ts';
import { usarDefinirTrilha } from '../lib/trilha.ts';
import { Icone } from '../componentes/Icone.tsx';
import { Valor } from '../componentes/Valor.tsx';
import { FILA, NIVEIS, SIGNIFICADO_DAS_SITUACOES, SITUACOES, diasAte, textoDoPrazo } from '../lib/rotulos.ts';
import { iniciais } from '../lib/iniciais.ts';
import '../estilos/clientes.css';
import '../estilos/sistema.css';
import '../estilos/ficha-cliente.css';

export function FichaCliente({
  id,
  aoVoltar,
  aoAbrirRelatorio,
  aoExpirarSessao,
}: {
  id: string;
  aoVoltar: () => void;
  aoAbrirRelatorio: (relatorioId: string) => void;
  aoExpirarSessao: () => void;
}) {
  const [ficha, setFicha] = useState<Ficha | null>(null);

  /**
   * Abre o relatório de um caso, criando se ainda não houver.
   *
   * ⚠️ **Uma função só, usada pelo título do caso e pelo nome do investigado.**
   * Duas cópias divergiriam na primeira correção feita em apenas uma delas — e
   * a segunda continuaria abrindo o relatório errado sem ninguém notar.
   */
  const abrirRelatorioDoCaso = useCallback(
    async (casoId: string) => {
      try {
        const r = await apiRelatorios.abrir(casoId);
        setInvestigadosDe(null);
        aoAbrirRelatorio(r.id);
      } catch (e) {
        setErro(e instanceof Error ? e.message : 'Não consegui abrir o relatório.');
      }
    },
    [aoAbrirRelatorio],
  );
  const [hoje, setHoje] = useState<string>('');
  const ehMaster = usarEhMaster();
  const [erro, setErro] = useState<string | null>(null);
  const [editando, setEditando] = useState(false);
  const [caso, setCaso] = useState<{ modo: 'novo' } | { modo: 'editar'; alvo: Caso } | null>(null);
  const [reuniaoDe, setReuniaoDe] = useState<{
    caso: Caso;
    inicial: { id: string; data: string; assunto: string; observacao: string | null } | null;
  } | null>(null);
  const [cobrancasDe, setCobrancasDe] = useState<Contrato | null>(null);
  const [fundoDe, setFundoDe] = useState<Caso | null>(null);
  const [aceitando, setAceitando] = useState<Contrato | null>(null);
  /**
   * Qual situação a lista de contratos está mostrando. `null` é o padrão:
   * proposta e ativo. Vem dos cinco números, que são botões — decisão do
   * Richard, 15/08/2026: *"pois se não eu não conseguirei ver o que já foi
   * concluído, recusado e etc (e nem voltar, se for o caso, para ativo)"*.
   */
  const [investigadosDe, setInvestigadosDe] = useState<Caso | null>(null);
  const [filtro, setFiltro] = useState<SituacaoContrato[] | null>(null);
  /** Muda a cada clique num número, para a seção abrir se estiver fechada. */
  const [abrirEm, setAbrirEm] = useState(0);
  const [contratoAberto, setContratoAberto] = useState<
    { modo: 'novo'; casoInicial: string | null; doPedido?: PropostaDoPedido } | { modo: 'editar'; alvo: Contrato } | null
  >(null);
  /* ⭐ O pedido de pesquisa (17/09/2026): o botão do topo, a lista que recarrega
     depois de montar a proposta, e o "Trazer do pedido" nos casos. */
  const [pedindoLink, setPedindoLink] = useState(false);
  const [versaoDosPedidos, setVersaoDosPedidos] = useState(0);
  const [temPedido, setTemPedido] = useState(false);
  const [trazendoPara, setTrazendoPara] = useState<Caso | null>(null);
  /* ⭐ Os documentos do pedido (18/09/2026, PRD §43.15): quantos cada caso tem —
     o botão "Recebido do cliente" só aparece onde há o que ver —, e o painel. */
  const [documentosPorCaso, setDocumentosPorCaso] = useState<Record<string, ContaDeDocumentos>>({});
  /* ⭐ O que o pedido do contrato tem e o caso ainda não (29/10/2026, PRD §74) — o aviso do cartão. */
  const [faltasPorCaso, setFaltasPorCaso] = useState<Record<string, FaltaDoPedido>>({});
  const [recebidoDe, setRecebidoDe] = useState<Caso | null>(null);
  const fecharPedidoDeLink = useCallback(() => setPedindoLink(false), []);
  const [confirmar, setConfirmar] = useState<{
    titulo: string;
    descricao: string;
    acao: () => Promise<unknown>;
  } | null>(null);

  /*
   * ⭐ **Apagar reunião pergunta antes — 19/09/2026.** Era o único apagar da
   * ficha que ia direto ao servidor, com um clique.
   */
  const pedirParaApagarReuniao = (c: Caso, reuniaoId: string) => {
    const r = c.reunioes.find((x) => x.id === reuniaoId);
    setConfirmar({
      titulo: 'Apagar reunião',
      descricao: !r
        ? 'A reunião sai do caso.'
        : r.realizada
          ? `A reunião realizada em ${paraTela(r.data)} (${r.assunto}) sai do caso e deixa de contar nas reuniões usadas.`
          : `A reunião marcada para ${paraTela(r.data)} (${r.assunto}) sai do caso e do calendário.`,
      acao: () => apiCasos.apagarReuniao(c.id, reuniaoId),
    });
  };

  const tratar = useCallback(
    (e: unknown) => {
      if (e instanceof ErroDaApi && e.status === 401) return aoExpirarSessao();
      avisarErro(e);
      setErro(e instanceof Error ? e.message : 'Algo não funcionou.');
    },
    [aoExpirarSessao],
  );

  const recarregar = useCallback(async () => {
    try {
      setErro(null);
      // "Hoje" vem do servidor, em horário de São Paulo — nunca do relógio de
      // quem abriu a tela.
      const [f, painel] = await Promise.all([apiClientes.ficha(id), api.painel()]);
      setFicha(f);
      setHoje(painel.hoje);
    } catch (e) {
      tratar(e);
    }
  }, [id, tratar]);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  // Só o master vê documento de pedido — como tudo do pedido. Uma consulta para a ficha inteira.
  useEffect(() => {
    if (!ehMaster || !ficha) return;
    let vivo = true;
    apiFormulario
      .documentosPorCaso(ficha.id)
      .then((r) => vivo && setDocumentosPorCaso(r.porCaso))
      .catch(() => undefined);
    apiFormulario
      .faltasPorCaso(ficha.id)
      .then((r) => vivo && setFaltasPorCaso(r.porCaso))
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, [ehMaster, ficha]);

  async function agir(acao: () => Promise<unknown>) {
    try {
      await acao();
      await recarregar();
    } catch (e) {
      tratar(e);
    }
  }

  /* ⭐ O caminho mora na barra superior desde 14/09/2026 (`lib/trilha.ts`). */
  usarDefinirTrilha(ficha ? [{ rotulo: 'Clientes', caminho: '/clientes' }, { rotulo: ficha.nome }] : null);

  if (!ficha) {
    return <div className="carregando">{erro ?? 'Carregando a ficha…'}</div>;
  }

  const ativos = ficha.casos.filter((c) => c.situacao !== 'encerrado');
  const fechados = ficha.casos.filter((c) => c.situacao === 'encerrado');

  /*
   * Os cinco números da ficha — desenho do Richard, 15/08/2026.
   *
   * ⚠️ **Proposta e contratado ficam SEPARADOS.** Somá-los faria uma proposta
   * de R$ 25.000 e um contrato de R$ 5.000 mostrarem R$ 30.000, e a ficha diria
   * que o cliente vale trinta mil quando ele contratou cinco.
   *
   * "Recebido" e "a receber" saíram daqui: vivem na aba Financeiro, que existe
   * para responder exatamente essas duas perguntas.
   *
   * Somas em centavos inteiros: somar `Number` de reais acumula erro de ponto
   * flutuante, e aqui isso viraria centavo errado na tela.
   */
  const emCentavos = (v: string) => Math.round(Number(v) * 100);
  // ⚠️ Sem esta conferência, o colaborador veria "R$ 0,00" no lugar de
  // "R$ ••••": `Number(null)` é zero, e a soma de nada daria um total que
  // parece verdadeiro. Total errado com cara de certo é pior que total nenhum.
  const semValores = ficha.contratos.some((c) => c.valoresOcultos);
  const somarPor = (...situacoes: SituacaoContrato[]) =>
    semValores
      ? null
      : ficha.contratos
          .filter((c) => situacoes.includes(c.situacao))
          .reduce((s, c) => s + emCentavos(c.valorTotal ?? '0'), 0) / 100;

  /** Quantos contratos há em cada situação — a soma sozinha não diz se é um ou seis. */
  const contar = (...situacoes: SituacaoContrato[]) => {
    const n = ficha.contratos.filter((c) => situacoes.includes(c.situacao)).length;
    return n === 1 ? '1 contrato' : `${n} contratos`;
  };

  /**
   * Clicar num número filtra a lista de contratos por aquela situação.
   *
   * Clicar de novo no mesmo volta ao padrão — o botão liga e desliga, como
   * qualquer filtro deve fazer. E a seção abre e recebe a rolagem: filtrar uma
   * lista que está fora da vista é obedecer sem mostrar.
   */
  function filtrarPor(situacoes: SituacaoContrato[]) {
    const igual =
      filtro !== null &&
      filtro.length === situacoes.length &&
      situacoes.every((s) => filtro.includes(s));
    setFiltro(igual ? null : situacoes);
    setAbrirEm((n) => n + 1);
    // O `requestAnimationFrame` espera a seção abrir; rolar antes iria para
    // onde ela ainda estava fechada.
    requestAnimationFrame(() =>
      document.getElementById('bloco-contratos')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      }),
    );
  }

  const selecionado = (situacoes: SituacaoContrato[]) =>
    filtro !== null &&
    filtro.length === situacoes.length &&
    situacoes.every((s) => filtro.includes(s));

  const totalPropostas = somarPor('proposta');
  const totalContratado = somarPor('ativo');
  const totalConcluido = somarPor('concluido');
  const totalPerdido = somarPor('recusada', 'cancelado');

  /* Se há alguma linha de cadastro para mostrar. Sem nenhuma, a folha diz. */
  const temCadastro = Boolean(
    ficha.contatoNome || ficha.email || ficha.telefone || ficha.origem || ficha.site ||
      ficha.logradouro || ficha.cidade || ficha.representanteNome,
  );

  function rolarAte(alvo: string) {
    document.getElementById(alvo)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <>
      {erro && <p className="erro">{erro}</p>}

      {/*
        ⭐⭐ **A FICHA DO CLIENTE NO SISTEMA VISUAL 2026 — 14/09/2026.**

        > *"Aprovo, pode construir."* — Richard, vendo a proposta desenhada
        > (`docs/sistema-visual-2026/proposta-ficha-do-cliente.png`)

        ⚠️ **Mudou o lugar e o desenho, não o que cada botão faz.** A trilha foi
        para a barra superior; a identidade, os botões de editar e apagar e os
        cinco números subiram para a abertura; o trabalho (casos e contratos)
        ficou na coluna larga e o relacionamento (cadastro, contatos e
        anotações) na estreita. ⚠️ **Casos vêm antes de contratos** — é o que
        se abre todo dia; o contrato se consulta.
      */}
      <div className="ficha-2026">
      <section className="s-folha s-identidade ficha-identidade">
        <div className="s-monograma ficha-monograma" aria-hidden="true">
          {iniciais(ficha.nome)}
        </div>
        <div className="ficha-identidade-texto">
          <span className="s-sobrelinha">Ficha do cliente</span>
          <h1 className="titulo">{ficha.nome}</h1>
          <p className="ficha-meta">
            <span className="ficha-tipo">{ficha.tipo === 'pj' ? 'PJ' : 'PF'}</span>
            <span className="ficha-doc">
              <span className="dado">{ficha.documentoFormatado}</span>
              <button
                type="button"
                className="ficha-copiar"
                onClick={() => {
                  void navigator.clipboard
                    ?.writeText(ficha.documentoFormatado)
                    .then(() => avisar(`${ficha.tipo === 'pj' ? 'CNPJ' : 'CPF'} copiado.`))
                    .catch(() => undefined);
                }}
                title="Copiar o documento"
                aria-label="Copiar o documento"
              >
                <Icone nome="copiar" tamanho={14} />
              </button>
            </span>
            {ficha.nomeFantasia && <span className="ficha-meta-item">{ficha.nomeFantasia}</span>}
            {(ficha.cidade || ficha.uf) && (
              <span className="ficha-meta-item">
                <Icone nome="lugar" tamanho={14} />
                {[ficha.cidade, ficha.uf].filter(Boolean).join('/')}
              </span>
            )}
            <span className="ficha-meta-item">
              <Icone nome="relogio" tamanho={14} />
              cliente desde <span className="dado">{paraTela(ficha.criadoEm.slice(0, 10))}</span>
            </span>
          </p>
        </div>
        {ehMaster && (
          <div className="acoes-do-cliente">
            {/*
              ⚠️ **Existe para o cadastro feito por engano, e só.** Por isso o
              servidor recusa quando há caso ou contrato pendurado: caso é
              trabalho e contrato é dinheiro, e nenhum dos dois some junto com
              um cadastro. A recusa vem com a mensagem dizendo o que apagar
              antes — decisão do Richard, 18/08/2026.
            */}
            <button
              type="button"
              className="s-acao perigo"
              onClick={() =>
                setConfirmar({
                  titulo: `Apagar ${ficha.nome}`,
                  descricao:
                    'O cadastro do cliente sai do Hub, com os contatos, as anotações e os pedidos de pesquisa dele (o link de pedido é desligado). ' +
                    'Não há como desfazer. Se houver caso ou contrato, o Hub recusa e diz o que apagar antes.',
                  acao: async () => {
                    await apiClientes.apagar(ficha.id);
                    // Voltar à lista: a ficha que se estava vendo não existe mais.
                    aoVoltar();
                  },
                })
              }
            >
              <Icone nome="lixeira" tamanho={14} />
              Apagar cliente
            </button>
            {/* ⭐ O link de pedido de pesquisa (17/09/2026, PRD §43.13): um por cliente, não vence. */}
            <button type="button" className="botao-secundario s-botao" onClick={() => setPedindoLink(true)}>
              <Icone nome="elo" tamanho={14} />
              Link de pedido
            </button>
            <button type="button" className="botao-secundario s-botao" onClick={() => setEditando(true)}>
              <Icone nome="lapis" tamanho={14} />
              Editar
            </button>
          </div>
        )}
      </section>

      {/*
        A ficha em cinco números — desenho do Richard, 15/08/2026.

        Lê-se da esquerda para a direita como a vida de um negócio: o que foi
        proposto, o que virou contrato, o que terminou, o que se perdeu, e o
        trabalho que corre agora.

        ⚠️ "Recebido" e "a receber" **não estão aqui de propósito**: são
        pergunta de caixa, e caixa é a aba Financeiro. Repetir os dois aqui
        criaria dois lugares para conferir o mesmo número.
      */}
      <div className="s-indicadores ficha-indicadores">
        {[
          { rotulo: 'Propostas', icone: 'proposta' as const, total: totalPropostas, situacoes: ['proposta'] as SituacaoContrato[] },
          { rotulo: 'Contratados', icone: 'contrato' as const, total: totalContratado, situacoes: ['ativo'] as SituacaoContrato[] },
          { rotulo: 'Concluídos', icone: 'concluido' as const, total: totalConcluido, situacoes: ['concluido'] as SituacaoContrato[] },
          {
            rotulo: 'Recusados · cancelados',
            icone: 'cancelado' as const,
            total: totalPerdido,
            situacoes: ['recusada', 'cancelado'] as SituacaoContrato[],
          },
        ].map((n) => (
          <button
            key={n.rotulo}
            type="button"
            className="s-indicador"
            onClick={() => filtrarPor(n.situacoes)}
            aria-pressed={selecionado(n.situacoes)}
          >
            <span className="s-micro">
              <Icone nome={n.icone} tamanho={13} />
              {n.rotulo}
            </span>
            <Valor
              de={n.total}
              className={'s-indicador-valor' + (n.total === 0 ? ' apagado' : '')}
            />
            <span className="s-indicador-nota">{contar(...n.situacoes)}</span>
          </button>
        ))}

        {/*
          ⚠️ **O quinto número leva aos CASOS, não filtra contratos.** Ele conta
          outra coisa — trabalho, não venda —, e fazê-lo filtrar a lista de
          contratos seria responder outra pergunta que não a que ele faz. Clicar
          leva à seção de casos, que é onde a resposta dele mora.
        */}
        <button type="button" className="s-indicador" onClick={() => rolarAte('bloco-casos')}>
          <span className="s-micro">
            <Icone nome="caso" tamanho={13} />
            Casos ativos
          </span>
          <span className={'s-indicador-valor' + (ativos.length === 0 ? ' apagado' : '')}>
            {ativos.length}
          </span>
          <span className="s-indicador-nota">
            {ficha.casos.length === ativos.length
              ? 'nenhum encerrado'
              : `${ficha.casos.length} no total`}
          </span>
        </button>
      </div>

      {ehMaster && (
        <PedidosDoCliente
          clienteId={ficha.id}
          clienteNome={ficha.nome}
          versao={versaoDosPedidos}
          gerandoLink={pedindoLink}
          aoFecharLink={fecharPedidoDeLink}
          aoMudar={setTemPedido}
          aoExpirarSessao={aoExpirarSessao}
          aoMontarProposta={(p) => setContratoAberto({ modo: 'novo', casoInicial: null, doPedido: p })}
        />
      )}

      {/* Atalhos para as partes da ficha — levam até a seção, não trocam de tela. */}
      <nav className="ficha-atalhos" aria-label="Partes da ficha">
        {[
          ['bloco-casos', 'Casos', ficha.casos.length],
          ['bloco-contratos', 'Contratos', ficha.contratos.length],
          ['bloco-cadastro', 'Cadastro', null],
          ['bloco-contatos', 'Contatos', ficha.contatos.length],
          ['bloco-anotacoes', 'Anotações', ficha.anotacoes.length],
        ].map(([alvo, rotulo, n]) => (
          <button key={alvo as string} type="button" onClick={() => rolarAte(alvo as string)}>
            {rotulo}
            {n !== null && <span className="dado">{n}</span>}
          </button>
        ))}
      </nav>

      <div className="ficha-2026-grade">
      <div className="ficha-2026-trabalho">
      <Secao
        id="casos"
        desenho="folha"
        titulo="Casos"
        subtitulo="O trabalho contratado."
        contador={ficha.casos.length}
        acao={
          <button type="button" className="botao-primario s-botao s-botao-p" onClick={() => setCaso({ modo: 'novo' })}>
            <Icone nome="mais" tamanho={14} />
            Novo caso
          </button>
        }
      >
        {ficha.casos.length === 0 ? (
          <p className="vazio">Nenhum caso ainda para este cliente.</p>
        ) : (
          <>
            {ativos.map((c) => (
              <CartaoCaso
                key={c.id}
                caso={c}
                hoje={hoje}
                aoMover={(s) => agir(() => apiCasos.mover(c.id, s))}
                aoEditar={() => setCaso({ modo: 'editar', alvo: c })}
                aoAbrirRelatorio={aoAbrirRelatorio}
                aoAbrirDoCaso={() => abrirRelatorioDoCaso(c.id)}
                aoAbrirInvestigados={() => setInvestigadosDe(c)}
                aoRegistrarReuniao={() => setReuniaoDe({ caso: c, inicial: null })}
                aoApagarReuniao={(rid) => pedirParaApagarReuniao(c, rid)}
                aoMarcarReuniao={(rid, feita) => agir(() => apiCasos.marcarReuniao(c.id, rid, feita))}
                aoEditarReuniao={(r) => setReuniaoDe({ caso: c, inicial: r })}
                aoAbrirFundo={() => setFundoDe(c)}
                aoTrazerDoPedido={ehMaster && (temPedido || !!faltasPorCaso[c.id]) ? () => setTrazendoPara(c) : undefined}
                faltaDoPedido={ehMaster ? faltasPorCaso[c.id] : undefined}
                documentosDoCliente={ehMaster ? documentosPorCaso[c.id] : undefined}
                aoAbrirDocumentos={() => setRecebidoDe(c)}
            aoCriarContrato={() => setContratoAberto({ modo: 'novo', casoInicial: c.id })}
                aoApagar={() =>
                  setConfirmar({
                    titulo: `Apagar o caso ${c.codigo}`,
                    descricao:
                      'O caso, suas reuniões, a trilha de situações e os compromissos que ele criou no calendário somem juntos.',
                    acao: () => apiCasos.apagar(c.id),
                  })
                }
              />
            ))}

            {fechados.length > 0 && (
              <>
                <p className="rotulo separador-encerrados">Encerrados · {fechados.length}</p>
                {fechados.map((c) => (
                  <CartaoCaso
                    key={c.id}
                    caso={c}
                    hoje={hoje}
                    aoMover={(s) => agir(() => apiCasos.mover(c.id, s))}
                    aoEditar={() => setCaso({ modo: 'editar', alvo: c })}
                    aoAbrirRelatorio={aoAbrirRelatorio}
                    aoAbrirDoCaso={() => abrirRelatorioDoCaso(c.id)}
                aoAbrirInvestigados={() => setInvestigadosDe(c)}
                aoRegistrarReuniao={() => setReuniaoDe({ caso: c, inicial: null })}
                    aoApagarReuniao={(rid) => pedirParaApagarReuniao(c, rid)}
                    aoMarcarReuniao={(rid, feita) =>
                      agir(() => apiCasos.marcarReuniao(c.id, rid, feita))
                    }
                    aoEditarReuniao={(r) => setReuniaoDe({ caso: c, inicial: r })}
                    aoAbrirFundo={() => setFundoDe(c)}
                    aoTrazerDoPedido={ehMaster && (temPedido || !!faltasPorCaso[c.id]) ? () => setTrazendoPara(c) : undefined}
                    faltaDoPedido={ehMaster ? faltasPorCaso[c.id] : undefined}
                    documentosDoCliente={ehMaster ? documentosPorCaso[c.id] : undefined}
                    aoAbrirDocumentos={() => setRecebidoDe(c)}
            aoCriarContrato={() => setContratoAberto({ modo: 'novo', casoInicial: c.id })}
                    aoApagar={() =>
                      setConfirmar({
                        titulo: `Apagar o caso ${c.codigo}`,
                        descricao:
                          'O caso, suas reuniões, a trilha de situações e os compromissos que ele criou no calendário somem juntos.',
                        acao: () => apiCasos.apagar(c.id),
                      })
                    }
                  />
                ))}
              </>
            )}
          </>
        )}
      </Secao>

      <SecaoContratos
        contratos={ficha.contratos}
        hoje={hoje}
        filtro={filtro}
        aoLimparFiltro={() => setFiltro(null)}
        abrirEm={abrirEm}
        aoNovo={() => setContratoAberto({ modo: 'novo', casoInicial: null })}
        aoEditar={(c) => setContratoAberto({ modo: 'editar', alvo: c })}
        aoCorrigirCobrancas={(c) => setCobrancasDe(c)}
        aoAceitar={(c) => setAceitando(c)}
        aoBaixar={(ctId, pId, pagar) =>
          agir(() => apiContratos.baixarParcela(ctId, pId, pagar ? hoje : null))
        }
        aoMudarSituacao={(ctId, s) => agir(() => apiContratos.editar(ctId, { situacao: s }))}
        aoApagar={(ctId) =>
          setConfirmar({
            titulo: 'Apagar contrato',
            descricao:
              'O contrato e todas as cobranças dele somem. Isso não pode ser desfeito.',
            acao: () => apiContratos.apagar(ctId),
          })
        }
      />
      </div>

      <div className="ficha-2026-relacao">
      <section id="bloco-cadastro" className="bloco s-folha secao-folha ficha-cadastro">
        <div className="bloco-topo">
          <h2 className="titulo-secao">Cadastro</h2>
          {ehMaster && (
            <button type="button" className="s-acao" onClick={() => setEditando(true)}>
              <Icone nome="lapis" tamanho={14} />
              Editar
            </button>
          )}
        </div>
        <div className="secao-corpo">
          {temCadastro ? (
            <dl className="s-props">
              {ficha.contatoNome && (
                <div className="s-prop">
                  <Icone nome="pessoa" tamanho={16} />
                  <dt>Fala-se com</dt>
                  <dd>
                    {ficha.contatoNome}
                    {ficha.contatoCargo && <small>{ficha.contatoCargo}</small>}
                  </dd>
                </div>
              )}
              {ficha.email && (
                <div className="s-prop">
                  <Icone nome="email" tamanho={16} />
                  <dt>E-mail</dt>
                  <dd>{ficha.email}</dd>
                </div>
              )}
              {ficha.telefone && (
                <div className="s-prop">
                  <Icone nome="telefone" tamanho={16} />
                  <dt>Telefone</dt>
                  <dd className="dado">{ficha.telefone}</dd>
                </div>
              )}
              {ficha.origem && (
                <div className="s-prop">
                  <Icone nome="indicou" tamanho={16} />
                  <dt>Quem indicou</dt>
                  <dd>{ficha.origem}</dd>
                </div>
              )}
              {ficha.site && (
                <div className="s-prop">
                  <Icone nome="globo" tamanho={16} />
                  <dt>Site</dt>
                  <dd>{ficha.site}</dd>
                </div>
              )}
              {(ficha.logradouro || ficha.cidade) && (
                <div className="s-prop">
                  <Icone nome="lugar" tamanho={16} />
                  <dt>Endereço</dt>
                  <dd>
                    {[
                      [ficha.logradouro, ficha.numero].filter(Boolean).join(', '),
                      ficha.complemento,
                      ficha.bairro,
                      [ficha.cidade, ficha.uf].filter(Boolean).join('/'),
                      ficha.cep,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </dd>
                </div>
              )}
              {ficha.representanteNome && (
                <div className="s-prop">
                  <Icone nome="assina" tamanho={16} />
                  <dt>Assina pela empresa</dt>
                  <dd>
                    {ficha.representanteNome}
                    {ficha.representanteCargo && <small>{ficha.representanteCargo}</small>}
                  </dd>
                </div>
              )}
            </dl>
          ) : (
            <p className="vazio">Sem contato, e-mail ou endereço cadastrado.</p>
          )}

          {ficha.observacao && (
            <p className="observacao-cliente">
              <span className="s-micro">Observação</span>
              {ficha.observacao}
            </p>
          )}
        </div>
      </section>

      <SecaoContatos
        contatos={ficha.contatos}
        aoCriar={async (dados) => {
          await apiClientes.criarContato(ficha.id, dados);
          await recarregar();
        }}
        aoEditar={async (cid, dados) => {
          await apiClientes.editarContato(ficha.id, cid, dados);
          await recarregar();
        }}
        aoApagar={(cid) =>
          setConfirmar({
            titulo: 'Apagar contato',
            descricao: 'O contato sai da ficha. Os contratos e casos não são afetados.',
            acao: () => apiClientes.apagarContato(ficha.id, cid),
          })
        }
      />

      <SecaoAnotacoes
        anotacoes={ficha.anotacoes}
        aoCriar={async (texto) => {
          await apiClientes.criarAnotacao(ficha.id, texto);
          avisar('Anotação registrada.');
          await recarregar();
        }}
        aoEditar={async (aid, texto) => {
          await apiClientes.editarAnotacao(ficha.id, aid, texto);
          avisar('Anotação corrigida.');
          await recarregar();
        }}
        aoApagar={(aid) =>
          setConfirmar({
            titulo: 'Apagar anotação',
            descricao: 'A anotação some do diário do relacionamento.',
            acao: () => apiClientes.apagarAnotacao(ficha.id, aid),
          })
        }
      />
      </div>
      </div>
      </div>

      {contratoAberto && (
        <FormularioContrato
          clienteId={ficha.id}
          clienteNome={ficha.nome}
          casos={ficha.casos}
          inicial={contratoAberto.modo === 'editar' ? contratoAberto.alvo : null}
          casoInicial={contratoAberto.modo === 'novo' ? contratoAberto.casoInicial : null}
          doPedido={contratoAberto.modo === 'novo' ? (contratoAberto.doPedido ?? null) : null}
          aoFechar={() => setContratoAberto(null)}
          aoSalvar={async (dados) => {
            if (contratoAberto.modo === 'editar') {
              /*
               * ⚠️ **Manda TUDO que o formulário mostra, ou a edição mente.**
               *
               * Esta chamada enviava só objeto, assinatura, observação e casos.
               * O Richard trocou o nível de uma proposta em 15/08/2026, salvou,
               * e a tela avisou "Contrato atualizado" — sem ter atualizado
               * nada. Campo que aparece na tela e não é enviado é pior que
               * campo que não existe: a pessoa confia que gravou.
               *
               * **`situacao` fica de fora de propósito.** Quem a muda é o
               * seletor do cartão. Mandada daqui, editar um contrato concluído
               * o devolveria a Ativo sem ninguém pedir.
               *
               * **`valorTotal` só na proposta**, porque no contrato ativo ele
               * tem de fechar com as parcelas — e quem mexe nelas é "Corrigir
               * cobranças".
               */
              const ehProposta = contratoAberto.alvo.ehProposta;
              await apiContratos.editar(contratoAberto.alvo.id, {
                objeto: dados.objeto,
                /* ⭐ **O que o cliente está contratando — 28/08/2026.** Uma
                   linha por pesquisa, com nível, honorário, teto e investigado
                   próprios. Substituiu `nivel`, `investigados` e `entendimento`
                   soltos, que só sabiam falar de uma pesquisa. */
                itens: dados.itens,
                /* ⛔ **Faltava, e a caixa marcada não chegava ao servidor —
                   11/09/2026.** Marcar Cortesia e salvar não mudava nada: o
                   campo existia no formulário, no banco e na rota, e não no
                   corpo do pedido. Construído, ligado, e sem o fio do meio. */
                cortesia: dados.cortesia,
                validadeDias: dados.validadeDias,
                condicoesPagamento: dados.condicoesPagamento ?? null,
                assinadoEm: dados.assinadoEm ?? null,
                observacao: dados.observacao ?? null,
                ...(ehProposta
                  ? {
                      valorTotal: dados.valorTotal,
                      fundoDeCustas: dados.fundoDeCustas ?? null,
                    }
                  : {}),
                // Proposta não vincula caso; o servidor recusaria a lista.
                ...(ehProposta ? {} : { casoIds: dados.casoIds ?? [] }),
              });
              avisar(ehProposta ? 'Proposta atualizada.' : 'Contrato atualizado.');
            } else {
              await apiContratos.criar(dados);
              avisar(dados.pedidoId ? 'Proposta montada a partir do pedido.' : 'Contrato criado.');
              if (dados.pedidoId) setVersaoDosPedidos((v) => v + 1);
            }
            setContratoAberto(null);
            await recarregar();
          }}
        />
      )}

      {recebidoDe && <RecebidoDoCliente casoId={recebidoDe.id} casoCodigo={recebidoDe.codigo} aoFechar={() => setRecebidoDe(null)} />}

      {trazendoPara && (
        <TrazerDoPedido
          casoId={trazendoPara.id}
          casoCodigo={trazendoPara.codigo}
          aoFechar={() => setTrazendoPara(null)}
          aoTrazido={() => {
            setTrazendoPara(null);
            void recarregar();
          }}
        />
      )}

      {investigadosDe && (
        <Investigados
          caso={investigadosDe}
          aoFechar={() => setInvestigadosDe(null)}
          /*
            ⚠️ **Recarrega o caso a cada mudança na ficha — 20/08/2026.**
            É o que faz o "Investigados N de 22" do cartão acompanhar o cadastro
            sem depender de F5. O Richard cadastrou três e o cartão continuava
            em "1": o número não estava errado, estava velho.
          */
          aoMudarAFicha={() => void recarregar()}
          /*
           * ⚠️ **Clicar no nome ABRE o relatório — e o cria se ainda não houver.**
           * Pedido do Richard, 18/08/2026. `apiRelatorios.abrir` é criar-ou-
           * devolver: um relatório por caso, e pedir o segundo devolve o que
           * existe. Então o clique nunca duplica nada.
           *
           * ⚠️ **É diferente do botão do código do relatório**, logo ao lado no
           * cartão, que só aparece depois de o relatório existir. Aquele é
           * atalho; este é o caminho do trabalho — a ficha do investigado é o
           * primeiro bloco do relatório, e é para lá que se vai depois de
           * preenchê-la.
           */
          aoAbrirRelatorio={async () => {
            try {
              const r = await apiRelatorios.abrir(investigadosDe.id);
              setInvestigadosDe(null);
              aoAbrirRelatorio(r.id);
            } catch (e) {
              setErro(e instanceof Error ? e.message : 'Não consegui abrir o relatório.');
            }
          }}
        />
      )}

      {editando && (
        <FormularioCliente
          inicial={ficha}
          aoFechar={() => setEditando(false)}
          aoSalvar={async (dados) => {
            await apiClientes.editar(ficha.id, dados);
            setEditando(false);
            await recarregar();
          }}
        />
      )}

      {caso && (
        <FormularioCaso
          clienteId={ficha.id}
          clienteNome={ficha.nome}
          contratos={ficha.contratos}
          inicial={caso.modo === 'editar' ? caso.alvo : null}
          aoFechar={() => setCaso(null)}
          aoSalvar={async (dados) => {
            if (caso.modo === 'editar') {
              const { clienteId: _ignorado, ...resto } = dados;
              await apiCasos.editar(caso.alvo.id, resto);
            } else {
              const novo = await apiCasos.criar(dados);
              avisar(novo.doPedido ? `Caso ${novo.codigo} criado com o que veio do pedido: ${oQueVeio(novo.doPedido)}.` : `Caso ${novo.codigo} criado.`);
              if (novo.doPedido?.notasNaoCouberam) {
                avisar('O que o cliente informou não coube na observação do caso (4.000 caracteres). Leia no pedido e resuma à mão.', 'ruim');
              }
            }
            setCaso(null);
            await recarregar();
          }}
        />
      )}

      {aceitando && (
        <AceitarProposta
          contrato={aceitando}
          aoFechar={() => setAceitando(null)}
          aoAceitar={async () => {
            avisar('Proposta aceita. As cobranças foram geradas.');
            await recarregar();
          }}
        />
      )}

      {fundoDe && (
        <FundoDeCustas
          caso={fundoDe}
          aoFechar={() => setFundoDe(null)}
          aoMudar={() => void recarregar()}
        />
      )}

      {cobrancasDe && (
        <CorrigirCobrancas
          contrato={cobrancasDe}
          aoFechar={() => setCobrancasDe(null)}
          aoSalvar={async (parcelas, valorTotal) => {
            await apiContratos.refazerParcelas(cobrancasDe.id, parcelas, valorTotal);
            avisar(valorTotal ? 'Total e cobranças corrigidos.' : 'Cobranças corrigidas.');
            setCobrancasDe(null);
            await recarregar();
          }}
        />
      )}

      {reuniaoDe && (
        <RegistrarReuniao
          caso={reuniaoDe.caso}
          inicial={reuniaoDe.inicial}
          aoFechar={() => setReuniaoDe(null)}
          aoSalvar={async (dados) => {
            if (reuniaoDe.inicial) {
              await apiCasos.editarReuniao(reuniaoDe.caso.id, reuniaoDe.inicial.id, dados);
              avisar('Reunião corrigida.');
            } else {
              await apiCasos.registrarReuniao(reuniaoDe.caso.id, dados);
              avisar('Reunião registrada.');
            }
            setReuniaoDe(null);
            await recarregar();
          }}
        />
      )}

      {confirmar && (
        <Confirmar
          titulo={confirmar.titulo}
          descricao={confirmar.descricao}
          aoFechar={() => setConfirmar(null)}
          aoConfirmar={() =>
            void agir(async () => {
              await confirmar.acao();
              avisar('Apagado.');
            })
          }
        />
      )}
    </>
  );
}

/**
 * A etapa que só o master pode escolher, porque mexe no relatório: ir para
 * Entregue quando o relatório ainda não foi entregue, e voltar para antes da
 * entrega quando ele já foi. Espelho de `efeitoNoRelatorio`
 * (`api/src/lib/entrega-do-relatorio.ts`) — se um mudar, o outro muda junto.
 */
const ANTES_DA_ENTREGA: SituacaoCaso[] = ['contratado', 'briefing', 'levantamento', 'analise', 'relatorio'];
function soDoMaster(nova: SituacaoCaso, relatorio: string | null): boolean {
  if (relatorio === null) return false;
  return nova === 'entregue' ? relatorio !== 'entregue' : ANTES_DA_ENTREGA.includes(nova) && relatorio === 'entregue';
}

const diaAnoDoPedido = (iso: string) =>
  new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(iso));

/** "1 investigado · a dívida (valor da dívida, …) · o que o cliente informou". */
function oQueFalta(f: { investigados: number; marcos: string[]; notas: boolean }): string {
  const partes: string[] = [];
  if (f.investigados) partes.push(f.investigados === 1 ? '1 investigado' : `${f.investigados} investigados`);
  if (f.marcos.length) partes.push(`a dívida (${f.marcos.map((m) => m.toLowerCase()).join(', ')})`);
  if (f.notas) partes.push('o que o cliente informou');
  return partes.join(' · ');
}

/** O aviso depois de gravar: o que entrou junto com o caso. */
function oQueVeio(t: { recebidoEm: string; investigados: number; marcos: string[]; notas: boolean }): string {
  return oQueFalta(t) || 'nada que o caso já não tivesse';
}

function CartaoCaso({
  caso,
  hoje,
  aoMover,
  aoEditar,
  aoRegistrarReuniao,
  aoApagarReuniao,
  aoMarcarReuniao,
  aoEditarReuniao,
  aoCriarContrato,
  aoAbrirFundo,
  aoAbrirRelatorio,
  aoAbrirDoCaso,
  aoAbrirInvestigados,
  aoTrazerDoPedido,
  faltaDoPedido,
  documentosDoCliente,
  aoAbrirDocumentos,
  aoApagar,
}: {
  caso: Caso;
  hoje: string;
  /** Abre o relatório deste caso, criando se ainda não houver. */
  aoAbrirDoCaso: () => void;
  aoMover: (s: SituacaoCaso) => void;
  aoEditar: () => void;
  aoRegistrarReuniao: () => void;
  aoApagarReuniao: (id: string) => void;
  aoMarcarReuniao: (id: string, realizada: boolean) => void;
  aoEditarReuniao: (r: { id: string; data: string; assunto: string; observacao: string | null }) => void;
  aoCriarContrato: () => void;
  aoAbrirFundo: () => void;
  aoAbrirRelatorio: (relatorioId: string) => void;
  aoAbrirInvestigados: () => void;
  /** ⭐ Só quando o cliente tem pedido de pesquisa, e só para o master (17/09/2026). */
  aoTrazerDoPedido?: () => void;
  /** ⭐ O que o pedido do contrato tem e o caso ainda não (29/10/2026). Só o master. */
  faltaDoPedido?: FaltaDoPedido;
  /** ⭐ Os documentos que vieram pelo pedido deste caso (18/09/2026). Sem nenhum, sem botão. */
  documentosDoCliente?: ContaDeDocumentos;
  aoAbrirDocumentos?: () => void;
  aoApagar: () => void;
}) {
  const dias = caso.prazoEm && hoje ? diasAte(caso.prazoEm, hoje) : null;
  const atrasado = dias !== null && dias < 0 && caso.situacao !== 'encerrado';
  // O seletor de situação e o "Registrar reunião" ficam para todo mundo: são o
  // trabalho andando. Corrigir o caso e apagá-lo são do master.
  const ehMaster = usarEhMaster();
  /* Há quantos dias foi assinado — só quando a assinatura já aconteceu. */
  const assinouHa = caso.assinadoEm && hoje ? diasAte(hoje, caso.assinadoEm) : null;

  /**
   * "4 de 12" em segmentos, para ler o teto sem fazer conta. Passou do teto,
   * os segmentos a mais saem na cor de atraso — é o aviso de aditivo, visto.
   * Teto grande demais para desenhar (Customizado) fica só no número.
   */
  const segmentos = (n: number, teto: number | null) =>
    teto !== null && teto > 0 && teto <= 30 ? (
      <span className="s-segmentos" aria-hidden="true">
        {Array.from({ length: Math.max(teto, n) }, (_, i) => (
          <i key={i} className={i >= teto ? 'demais' : i < n ? 'cheio' : ''} />
        ))}
      </span>
    ) : null;

  return (
    <article
      className={
        /* ⭐ O cartão no sistema visual 2026 — 14/09/2026. */
        `caso caso-2026 nivel-${caso.nivel}` + (caso.situacao === 'encerrado' ? ' caso-encerrado' : '')
      }
    >
      <div className="caso-topo">
        <div className="caso-identidade">
          <span className="dado caso-codigo">{caso.codigo}</span>
          <span className={`selo-nivel selo-${caso.nivel}`}>{NIVEIS[caso.nivel].longo}</span>
          {atrasado && <span className="s-pilula s-pilula-vinho">Atrasado</span>}
          <span className="caso-sep" aria-hidden="true" />
          {caso.contrato ? (
            <span className="vinculo-contrato">
              <Icone nome="contrato" tamanho={13} />
              <span className="dado">{caso.contrato.codigo}</span>
            </span>
          ) : (
            <span className="s-pilula s-pilula-ouro">Sem contrato</span>
          )}
          {/* O relatório deste caso, clicável — a outra metade do vínculo que
              o Richard pediu em 16/08/2026. Só aparece depois de existir: um
              atalho para o que ainda não foi criado confunde mais que ajuda. */}
          {caso.relatorio && (
            <button
              type="button"
              className="vinculo-contrato link-interno"
              onClick={() => aoAbrirRelatorio(caso.relatorio!.id)}
              title="Abrir o relatório deste caso"
            >
              <Icone nome="relatorios" tamanho={13} />
              <span className="dado">{caso.relatorio.codigo}</span>
              <Icone nome="seta-fora" tamanho={12} />
            </button>
          )}
        </div>
      </div>

      <div className="caso-linha-titulo">
      {/*
        ⚠️ **O título do caso leva ao relatório** — pedido do Richard, 19/08/2026.
        Na prática o título é o nome do devedor principal, e é nele que a mão vai
        primeiro. Abre o relatório do caso, criando se ainda não houver: a rota é
        criar-ou-devolver, um por caso, então o clique nunca duplica.

        ⚠️ **Botão e não link**, pela mesma razão do nome do investigado: não há
        endereço próprio: o relatório é do caso. Um `<a href>` mentiria sobre
        para onde vai, e o leitor de tela leria a mentira junto.
      */}
      {caso.titulo && (
        <p className="caso-titulo">
          <button type="button" className="nome-que-abre" onClick={aoAbrirDoCaso}>
            {caso.titulo}
          </button>
        </p>
      )}

        <select
          className="caso-situacao s-seletor"
          value={caso.situacao}
          onChange={(e) => aoMover(e.target.value as SituacaoCaso)}
          aria-label={`Situação do caso ${caso.codigo}`}
          /* ⭐ O que a etapa atual significa — 14/09/2026 (`lib/rotulos.ts`). */
          title={`${SITUACOES[caso.situacao]}: ${SIGNIFICADO_DAS_SITUACOES[caso.situacao]}`}
        >
          {/* ⛔ **Entregar e desentregar é do master — 19/09/2026.** Com
              relatório no caso, ir para Entregue entrega e trava o relatório,
              e voltar de Entregue o destrava; o servidor recusa as duas ao
              colaborador (`efeitoNoRelatorio`) e o seletor as oferecia. Para
              ele, essas opções aparecem travadas, dizendo de quem são. As
              outras mudanças de etapa continuam dele. */}
          {FILA.map((s) => {
            const doMaster = !ehMaster && soDoMaster(s, caso.relatorio?.situacao ?? null);
            return (
              <option key={s} value={s} disabled={doMaster}>
                {SITUACOES[s]}
                {doMaster ? ' · só o master' : ''}
              </option>
            );
          })}
          <option value="suspenso">{SITUACOES.suspenso}</option>
        </select>

      </div>

      <div className="caso-miolo">

      {/* ⭐ A escada em bolinhas só MOSTRA a situação; quem a muda continua
          sendo o seletor acima. Decisão 20, 13/09/2026. */}
      <PercursoDoCaso situacao={caso.situacao} />

      <div className="s-fatos caso-fatos">
        {/*
          ⚠️ **O número é o da FICHA, e não o do contrato — 20/08/2026.**

          > *"Cadastrei 3 investigados no caso 2026-001, e no card do caso ainda
          > consta 'Investigados 1 de 22'. Precisava ser automático."* — Richard

          O "1" era `caso.investigados`, o **contratado**, que não sobe sozinho —
          nem deve: é dele que sai o aviso de aditivo, e é cláusula. O que faltava
          era mostrar a **contagem da ficha**, que é o que muda quando alguém
          cadastra.

          ⚠️ **Os dois números continuam existindo**, e o contratado aparece
          **quando divergem** — porque aí ele deixa de ser detalhe de cadastro e
          vira a notícia: a ficha passou do que foi vendido.
        */}
        <div className="s-fato">
          <span className="s-micro">Investigados</span>
          <span className="s-fato-valor">
            {caso.investigadosNaFicha}
            {caso.limites.investigados !== null && (
              <span className="de"> de {caso.limites.investigados}</span>
            )}
          </span>
          {segmentos(caso.investigadosNaFicha, caso.limites.investigados)}
          {/* ⚠️ **O "contratado para N" saiu em 20/08/2026**, junto com o campo
              que o alimentava. Sem alguém para digitá-lo, o valor guardado é o
              default `1` — e comparar a ficha com um default seria dar notícia
              de um número que ninguém declarou. O que vale agora é a ficha
              contra o teto do nível, e esse aviso vem do servidor. */}
        </div>
        <div className="s-fato">
          <span className="s-micro">Assinatura</span>
          <span className="s-fato-valor">
            {caso.assinadoEm ? paraTela(caso.assinadoEm) : '—'}
          </span>
          {assinouHa !== null && assinouHa >= 0 && (
            <span className="s-fato-nota">
              {assinouHa === 0 ? 'hoje' : `há ${assinouHa} ${assinouHa === 1 ? 'dia' : 'dias'}`}
            </span>
          )}
        </div>
        <div className="s-fato">
          <span className="s-micro">Entrega</span>
          <span className={'s-fato-valor' + (atrasado ? ' vencido' : '')}>
            {caso.prazoEm ? paraTela(caso.prazoEm) : '—'}
          </span>
          {dias !== null && caso.situacao !== 'encerrado' && (
            <span className={'s-fato-nota' + (atrasado ? ' vencido' : '')}>{textoDoPrazo(dias)}</span>
          )}
        </div>
        <div className="s-fato">
          <span className="s-micro">Valor</span>
          {/* ⛔ **Cortesia não tem valor a mostrar — 11/09/2026.** O cartão
              dizia "R$ 0,00" ao master e "R$ ••••" ao colaborador — o segundo
              pior: esconde um valor que não existe, e faz a pessoa perguntar
              por um dinheiro que ninguém combinou. */}
          {caso.contrato?.cortesia ? (
            <>
              <span className="s-fato-valor">cortesia</span>
              {caso.contrato.casos > 1 && (
                <span className="s-fato-nota">conjunto de {caso.contrato.casos}</span>
              )}
            </>
          ) : caso.contrato ? (
            <>
              <span className="s-fato-valor">
                <Valor de={caso.contrato.valorTotal} />
              </span>
              {/* Contrato que cobre mais de um caso: o valor é do conjunto.
                  Sem este aviso, pareceria que este caso sozinho custou tudo. */}
              {caso.contrato.casos > 1 && (
                <span className="s-fato-nota">conjunto de {caso.contrato.casos}</span>
              )}
            </>
          ) : ehMaster ? (
            <button type="button" className="botao-texto botao-vincular" onClick={aoCriarContrato}>
              criar contrato
            </button>
          ) : (
            // Caso sem contrato é pendência comercial, e o colaborador precisa
            // enxergá-la — só não é ele quem resolve.
            <span className="ajuda">sem contrato</span>
          )}
        </div>
        {caso.fundo && (
          <div className="s-fato">
            <span className="s-micro">Fundo de custas</span>
            <span className={'s-fato-valor' + (caso.fundo.estourou ? ' vencido' : '')}>
              <Valor de={caso.fundo.gasto} />
              {caso.fundo.teto && (
                <span className="de">
                  {' '}
                  de <Valor de={caso.fundo.teto} />
                </span>
              )}
            </span>
          </div>
        )}
        <div className="s-fato">
          <span className="s-micro">Reuniões</span>
          <span className="s-fato-valor">
            {caso.reunioesUsadas}
            {caso.limites.reunioes !== null && (
              <span className="de"> de {caso.limites.reunioes}</span>
            )}
          </span>
          {segmentos(caso.reunioesUsadas, caso.limites.reunioes)}
        </div>
      </div>

      {/* ⭐ O prazo numa linha, com o ponto de HOJE — só olho: as datas e o
          "faltam N dias" continuam escritos acima. Decisão 20, 13/09/2026. */}
      <PrazoNaLinha
        assinadoEm={caso.assinadoEm}
        prazoEm={caso.prazoEm}
        hoje={hoje}
        encerrado={caso.situacao === 'encerrado'}
      />

      {[...caso.avisos, ...caso.avisosDasDatas, ...(caso.fundo?.avisos ?? [])].length > 0 && (
        <ul className={'caso-avisos' + (caso.fundo?.estourou ? ' aviso-vencido' : '')}>
          {[...caso.avisos, ...caso.avisosDasDatas, ...(caso.fundo?.avisos ?? [])].map((a) => (
            <li key={a}>{a}</li>
          ))}
        </ul>
      )}

      {caso.observacao && <p className="caso-observacao">{caso.observacao}</p>}

      {caso.reunioes.length > 0 && (
        <ul className="caso-reunioes">
          {caso.reunioes.map((r) => (
            <li key={r.id} className={r.realizada ? '' : 'reuniao-agendada'}>
              <span className="dado">{paraTela(r.data)}</span>
              <span className="reuniao-assunto">
                {r.assunto}
                <span className="s-pilula s-pilula-quieta">{r.realizada ? 'realizada' : 'agendada'}</span>
              </span>
              {!r.realizada && (
                <button
                  type="button"
                  className="botao-texto"
                  onClick={() => aoMarcarReuniao(r.id, true)}
                >
                  Aconteceu
                </button>
              )}
              {/* "Aconteceu" fica para todo mundo — quem esteve na reunião é
                  quem sabe que ela aconteceu. Corrigir e apagar são do master. */}
              {ehMaster && (
                <>
                  <button type="button" className="botao-texto" onClick={() => aoEditarReuniao(r)}>
                    Editar
                  </button>
                  <button
                    type="button"
                    className="botao-texto botao-perigo"
                    onClick={() => aoApagarReuniao(r.id)}
                  >
                    Apagar
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      </div>

      {/* ⭐ O dado do pedido nunca fica para trás sem ninguém ver (29/10/2026, PRD §74). */}
      {ehMaster && faltaDoPedido && (
        <p className="imv-pendencias caso-falta-do-pedido" role="status">
          {faltaDoPedido.pesquisasSemCaso > 0 ? (
            <>
              <b>O pedido de pesquisa de {diaAnoDoPedido(faltaDoPedido.recebidoEm)}</b> tem {faltaDoPedido.pesquisasSemCaso} pesquisas que ainda não
              estão em caso nenhum.
            </>
          ) : (
            <>
              <b>O pedido de pesquisa de {diaAnoDoPedido(faltaDoPedido.recebidoEm)}</b> tem o que este caso ainda não tem: {oQueFalta(faltaDoPedido)}.
            </>
          )}{' '}
          {aoTrazerDoPedido && (
            <button type="button" className="botao-texto" onClick={aoTrazerDoPedido}>
              Trazer do pedido
            </button>
          )}
        </p>
      )}

      <div className="s-acoes caso-acoes">
        {/* Preencher a ficha do investigado é o trabalho do dia, como
            registrar reunião: fica para todo mundo, não só para o master. */}
        <button type="button" className="s-acao" onClick={aoAbrirInvestigados}>
          <Icone nome="pessoa" tamanho={14} />
          Investigados
        </button>
        <button type="button" className="s-acao" onClick={aoRegistrarReuniao}>
          <Icone nome="calendario" tamanho={14} />
          Registrar reunião
        </button>
        {/* O Fundo de Custas é dinheiro do cliente: só o master alcança. */}
        {ehMaster && (
          <button type="button" className="s-acao" onClick={aoAbrirFundo}>
            <Icone nome="fundo" tamanho={14} />
            Fundo de custas
          </button>
        )}
        {ehMaster && aoTrazerDoPedido && (
          <button type="button" className="s-acao" onClick={aoTrazerDoPedido}>
            <Icone nome="trazer" tamanho={14} />
            Trazer do pedido
          </button>
        )}
        {ehMaster && documentosDoCliente && aoAbrirDocumentos && (
          <button type="button" className="s-acao" onClick={aoAbrirDocumentos}>
            <Icone nome="caixa" tamanho={14} />
            Recebido do cliente · {documentosDoCliente.total}
            {documentosDoCliente.emConferencia > 0 && <span className="fraco"> · {documentosDoCliente.emConferencia} em conferência</span>}
          </button>
        )}
        {ehMaster && (
          <span className="dir">
            <button type="button" className="s-acao" onClick={aoEditar}>
              <Icone nome="lapis" tamanho={14} />
              Editar
            </button>
            <button type="button" className="s-acao perigo" onClick={aoApagar}>
              <Icone nome="lixeira" tamanho={14} />
              Apagar
            </button>
          </span>
        )}
      </div>
    </article>
  );
}
